import React, { useEffect, useMemo, useState } from "react";
import { Save, Loader2, Plus, Trash2, GripVertical, RotateCcw, ExternalLink } from "lucide-react";
import { adminApi } from "../../lib/api";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { useToast } from "../../hooks/use-toast";
import NavMenuPreview from "./NavMenuPreview";

const inputCls = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange/40";

const SOURCE_LABEL = { builtin: "Site page", page: "CMS page", custom: "Custom" };

export default function NavigationManager() {
  const { toast } = useToast();
  const [items, setItems] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dragId, setDragId] = useState(null);

  const load = () => adminApi.navigation.get().then((d) => setItems(d.items || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const groups = useMemo(() => (items || []).filter((i) => i.type === "group"), [items]);
  const locations = useMemo(
    () => [{ id: "none", label: "Top level (main menu bar)" }, ...groups.map((g) => ({ id: g.id, label: `${g.label} (dropdown)` }))],
    [groups]
  );

  const inLocation = (parent) =>
    (items || []).filter((i) => (i.parent || "none") === parent).sort((a, b) => (a.order || 0) - (b.order || 0));

  const patch = (id, changes) => setItems((list) => list.map((i) => (i.id === id ? { ...i, ...changes } : i)));

  const renumber = (list) => {
    const counters = {};
    return list.map((i) => {
      const p = i.parent || "none";
      counters[p] = counters[p] || 0;
      return i;
    });
  };

  // Move the dragged item into `parent`, positioned before `beforeId` (or last).
  const move = (parent, beforeId) => {
    if (!dragId) return;
    setItems((list) => {
      const dragged = list.find((i) => i.id === dragId);
      if (!dragged || dragged.id === beforeId) return list;
      if (dragged.type === "group" && parent !== "none") return list; // dropdowns live at the top level
      const target = list
        .filter((i) => (i.parent || "none") === parent && i.id !== dragId)
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      const idx = beforeId ? target.findIndex((i) => i.id === beforeId) : target.length;
      target.splice(idx < 0 ? target.length : idx, 0, { ...dragged, parent });
      const orders = {};
      target.forEach((i, n) => { orders[i.id] = n; });
      return list.map((i) => (orders[i.id] !== undefined ? { ...i, parent, order: orders[i.id] } : i));
    });
    setDragId(null);
  };

  const nudge = (id, dir) => {
    const item = (items || []).find((i) => i.id === id);
    const siblings = inLocation(item.parent || "none");
    const idx = siblings.findIndex((i) => i.id === id);
    const swap = siblings[idx + dir];
    if (!swap) return;
    setItems((list) => list.map((i) => {
      if (i.id === id) return { ...i, order: swap.order ?? idx + dir };
      if (i.id === swap.id) return { ...i, order: item.order ?? idx };
      return i;
    }));
  };

  const addItem = (type) => {
    const id = `custom:${Date.now().toString(36)}`;
    setItems((list) => [
      ...list,
      {
        id, source: "custom", type,
        label: type === "group" ? "New dropdown" : "New link",
        url: type === "group" ? "" : "/",
        parent: "none", order: 999, visible: true, badge: "", desc: "", icon: "",
      },
    ]);
  };

  const removeItem = (id) => setItems((list) => list.filter((i) => i.id !== id && (i.parent || "none") !== id));

  const save = async () => {
    setSaving(true);
    try {
      const d = await adminApi.navigation.save(renumber(items));
      setItems(d.items || []);
      toast({ title: "Menu saved", description: "The site header now uses this structure." });
    } catch {
      toast({ title: "Couldn't save the menu", variant: "destructive" });
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Reset the menu back to the built-in default structure?")) return;
    const d = await adminApi.navigation.reset();
    setItems(d.items || []);
    toast({ title: "Menu reset to defaults" });
  };

  if (!items) return <p className="text-sm text-slate-400">Loading…</p>;

  const Row = ({ item }) => (
    <div
      draggable
      onDragStart={() => setDragId(item.id)}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => { e.stopPropagation(); move(item.parent || "none", item.id); }}
      data-testid={`nav-row-${item.id}`}
      className={`rounded-xl border bg-white p-3 ${item.visible === false ? "border-slate-100 opacity-60" : "border-slate-200"}`}
    >
      <div className="flex items-start gap-3">
        <span className="text-slate-300 cursor-grab pt-2"><GripVertical size={16} /></span>
        <div className="flex-1 grid md:grid-cols-[1fr_1fr] gap-2">
          <input
            value={item.label || ""}
            onChange={(e) => patch(item.id, { label: e.target.value })}
            className={inputCls}
            placeholder="Menu label"
            data-testid={`nav-label-${item.id}`}
          />
          <select
            value={item.parent || "none"}
            onChange={(e) => patch(item.id, { parent: e.target.value, order: 999 })}
            className={inputCls}
            disabled={item.type === "group"}
            data-testid={`nav-parent-${item.id}`}
          >
            {locations.filter((l) => l.id !== item.id).map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
          </select>
          {item.source === "custom" && item.type !== "group" && (
            <input
              value={item.url || ""}
              onChange={(e) => patch(item.id, { url: e.target.value })}
              className={inputCls}
              placeholder="/page-url or https://…"
              data-testid={`nav-url-${item.id}`}
            />
          )}
          {item.source === "custom" && item.type !== "group" && (
            <input
              value={item.badge || ""}
              onChange={(e) => patch(item.id, { badge: e.target.value })}
              className={inputCls}
              placeholder="Badge (optional, e.g. Free)"
            />
          )}
        </div>
        <div className="flex items-center gap-2 pt-1 shrink-0">
          <button onClick={() => nudge(item.id, -1)} className="w-7 h-7 rounded-md bg-slate-100 hover:bg-slate-200 text-navy text-xs" title="Move up">↑</button>
          <button onClick={() => nudge(item.id, 1)} className="w-7 h-7 rounded-md bg-slate-100 hover:bg-slate-200 text-navy text-xs" title="Move down">↓</button>
          <Switch
            checked={item.visible !== false}
            onCheckedChange={(v) => patch(item.id, { visible: v })}
            data-testid={`nav-visible-${item.id}`}
          />
          {item.source === "custom" ? (
            <button onClick={() => removeItem(item.id)} className="w-7 h-7 rounded-md bg-red-50 hover:bg-red-100 text-red-500 flex items-center justify-center" data-testid={`nav-delete-${item.id}`}><Trash2 size={14} /></button>
          ) : <span className="w-7" />}
        </div>
      </div>
      <div className="pl-7 mt-1.5 flex items-center gap-3 text-[11px] text-slate-400">
        <span className="font-semibold uppercase tracking-wide">{SOURCE_LABEL[item.source] || item.source}</span>
        {item.url && <span className="flex items-center gap-1">{item.url}{/^https?:/.test(item.url) && <ExternalLink size={11} />}</span>}
        {item.type === "group" && <span>Dropdown — drag items onto it below</span>}
        {item.mega === "services" && <span>Service list is managed in Services</span>}
        {item.visible === false && <span className="text-orange font-semibold">Hidden</span>}
      </div>
    </div>
  );

  const Location = ({ loc }) => {
    const rows = inLocation(loc.id);
    return (
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={() => move(loc.id, null)}
        className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 mb-5"
        data-testid={`nav-location-${loc.id}`}
      >
        <h2 className="font-display font-bold text-navy mb-3 flex items-center gap-2">
          {loc.label}
          <span className="text-xs font-normal text-slate-400">{rows.length} item{rows.length === 1 ? "" : "s"}</span>
        </h2>
        <div className="space-y-2">
          {rows.map((r) => <Row key={r.id} item={r} />)}
          {!rows.length && <p className="text-xs text-slate-400 py-4 text-center border border-dashed border-slate-200 rounded-xl">Drag a menu item here</p>}
        </div>
      </div>
    );
  };

  return (
    <div data-testid="navigation-manager">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="font-display font-extrabold text-2xl text-navy">Navigation</h1>
          <p className="text-sm text-slate-500 max-w-2xl">
            Decide where every page sits in the header menu — drag items between the top level and any dropdown,
            rename labels, reorder, hide items, or add your own dropdowns and links. The mobile menu mirrors this automatically.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button onClick={reset} variant="outline" className="rounded-full" data-testid="nav-reset">
            <RotateCcw size={14} className="mr-1.5" /> Reset to defaults
          </Button>
          <Button onClick={save} disabled={saving} className="bg-orange hover:bg-orange-dark text-white rounded-full" data-testid="nav-save">
            {saving ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Save size={15} className="mr-1.5" />} Save menu
          </Button>
        </div>
      </div>

      <NavMenuPreview items={items} />

      <div className="flex items-center gap-2 mb-5">
        <Button onClick={() => addItem("link")} variant="outline" size="sm" className="rounded-full" data-testid="nav-add-link">
          <Plus size={14} className="mr-1" /> Add link
        </Button>
        <Button onClick={() => addItem("group")} variant="outline" size="sm" className="rounded-full" data-testid="nav-add-group">
          <Plus size={14} className="mr-1" /> Add dropdown
        </Button>
      </div>

      {locations.map((loc) => <Location key={loc.id} loc={loc} />)}
    </div>
  );
}
